import { useState } from 'react'

import { EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import {
  BenefitsEmployeeSelectorLegalEntitiesDocument,
  BenefitsEmployeeSelectorLegalEntityFragment,
  BenefitsNameSelectorDocument,
} from 'generated/resources/graphql'

import {
  mapEmployees,
  mapSelectedEmployees,
  mapToEmployeeFragment,
  subMapToEmployeeFragment,
} from 'modules/core/components/EntitySelectWrapper/Entities/Employees/lib/mappers'
import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { flatSelection } from 'modules/core/components/EntitySelectWrapper/lib/flat'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import { useActiveMarkets } from 'modules/benefits/hooks/useActiveMarkets'
import { groupByLegalEntities } from 'modules/benefits/pages/EmployeesPage/ActivateBenefitsModal/lib/benefitsEmployeeSelectorUtils'
import { GroupType, Groups } from 'modules/benefits/pages/EmployeesPage/ActivateBenefitsModal/types'

export function useBenefitsEmployeeSelector(
  companyId: number,
  employeeIdsWithBenefits: number[],
  onEmployeesSelected?: (employees: EmployeeForSelectFragmentNoAccess[]) => void
) {
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<
    EmployeeForSelectFragmentNoAccess[]
  >([])
  const [selectedGroup, setSelectedGroup] = useState<GroupType>(Groups.ALL)
  const [expandedElements, setExpandedElements] = useState<number[]>([])

  // Resolve first the active markets to know which LEs to fetch
  const { activeMarkets, loading: loadingActiveMarkets } = useActiveMarkets()

  const { data: legalEntitiesData, loading: loadingLegalEntities } = useQuery(
    BenefitsEmployeeSelectorLegalEntitiesDocument,
    {
      variables: { companyId, markets: activeMarkets },
      skip: loadingActiveMarkets,
      fetchPolicy: 'cache-first',
    }
  )
  const legalEntities = useUnsafeMemo(() => {
    return legalEntitiesData?.apiCore?.company?.legalEntities?.filter(isBenefitsEligible)
  }, [legalEntitiesData])

  const excludedIds = useUnsafeMemo(() => employeeIdsWithBenefits, [employeeIdsWithBenefits])
  const qualifiedEmployeeGroupIds = useUnsafeMemo(() => {
    const groupIds = legalEntities?.flatMap((legalEntity) =>
      legalEntity.employeeGroups.map((group) => group.id)
    )
    return groupIds
  }, [legalEntities])

  // Fetch the employees names for the selected LEs
  const { loading: loadingEmployeeNames, data: employeeNameData } = useQuery(
    BenefitsNameSelectorDocument,
    {
      variables: {
        companyId,
        active: true,
        legalEntityIds: legalEntities?.map((legalEntity) => legalEntity.id) ?? [],
      },
      skip: loadingActiveMarkets || loadingLegalEntities,
    }
  )

  const employeeNames = employeeNameData?.employees.employeeNames
  const employeeEntities = useUnsafeMemo(() => {
    const filteredEmployees = employeeNames?.filter(
      (employee) =>
        !excludedIds.includes(employee.id) &&
        employee.employeeGroupId &&
        qualifiedEmployeeGroupIds?.includes(employee.employeeGroupId)
    )

    return mapEmployees(filteredEmployees, [])
  }, [employeeNames, excludedIds, qualifiedEmployeeGroupIds])

  const legalEntitiesEntities = useUnsafeMemo(() => {
    const filteredEmployees = employeeNames?.filter(
      (employee) =>
        !excludedIds.includes(employee.id) &&
        employee.employeeGroupId &&
        qualifiedEmployeeGroupIds?.includes(employee.employeeGroupId)
    )

    const result = groupByLegalEntities(filteredEmployees, legalEntities, [], expandedElements)

    return result
  }, [employeeNames, legalEntities, expandedElements, excludedIds, qualifiedEmployeeGroupIds])

  const mapSelectedEmployeesWithGroupType = (
    group: GroupType,
    selectedIds: EmployeeForSelectFragmentNoAccess[],
    entities: EntitySelectEntity[]
  ) => {
    if (group === Groups.ALL) {
      return mapSelectedEmployees('all', selectedIds, entities)
    }

    if (group === Groups.LEGAL_ENTITIES) {
      const employeeIds = new Set(selectedIds.map((emp) => Number(emp.id)))

      const mappedEntities = entities
        .map((entity) => {
          const selectedSubItems =
            entity.subItems?.filter((subItem) => employeeIds.has(Number(subItem.subId))) || []

          return {
            ...entity,
            subItems: selectedSubItems,
          }
        })
        .filter((entity) => entity.subItems && entity.subItems.length > 0)

      return mappedEntities
    }

    return []
  }

  const selectedEmployees = useUnsafeMemo(
    () => mapSelectedEmployeesWithGroupType(selectedGroup, selectedEmployeeIds, employeeEntities),
    [selectedGroup, selectedEmployeeIds, employeeEntities]
  )

  const selectedLegalEntities = useUnsafeMemo(
    () =>
      mapSelectedEmployeesWithGroupType(selectedGroup, selectedEmployeeIds, legalEntitiesEntities),
    [selectedGroup, selectedEmployeeIds, legalEntitiesEntities]
  )

  const groupMap: Record<GroupType, EntitySelectEntity[]> = useUnsafeMemo(
    () => ({
      all: employeeEntities,
      legalEntities: legalEntitiesEntities,
    }),
    [employeeEntities, legalEntitiesEntities]
  )

  const selectedGroupMap: Record<GroupType, EntitySelectEntity[]> = useUnsafeMemo(
    () => ({
      all: selectedEmployees ?? [],
      legalEntities: selectedLegalEntities ?? [],
    }),
    [selectedEmployees, selectedLegalEntities]
  )

  const onItemExpandedChange = (id: number, expanded: boolean) => {
    if (expanded) {
      setExpandedElements([id].concat(expandedElements))
    } else {
      setExpandedElements(expandedElements.filter((el) => el !== id))
    }
  }

  const onSelect = (selection: EntitySelectEntity[] | EntitySelectEntity | null) => {
    let newSelectedEmployees: EmployeeForSelectFragmentNoAccess[] = []

    if (selection === null) {
      newSelectedEmployees = []
    } else if (Array.isArray(selection)) {
      if (selectedGroup === Groups.ALL) {
        newSelectedEmployees = selection.map(mapToEmployeeFragment)
      } else {
        const flatSelected = flatSelection(selection)
        newSelectedEmployees = flatSelected.map(subMapToEmployeeFragment).filter(Boolean)
      }
    }

    setSelectedEmployeeIds(newSelectedEmployees)
    onEmployeesSelected?.(newSelectedEmployees)
  }

  const loading = loadingActiveMarkets || loadingLegalEntities || loadingEmployeeNames

  return {
    loading,
    selectedEmployeeIds,
    setSelectedEmployeeIds,
    selectedGroup,
    setSelectedGroup,
    expandedElements,
    onItemExpandedChange,
    onSelect,
    groupMap,
    selectedGroupMap,
    legalEntities,
  }
}

function isBenefitsEligible(legalEntity: BenefitsEmployeeSelectorLegalEntityFragment): boolean {
  if (legalEntity.benefitsOnboardings.totalCount) return true

  return legalEntity.expensesBankingAccounts?.some((account) =>
    account.bankAccounts?.some((bankAccount) =>
      bankAccount.cardholders.some(
        (cardholder) => (cardholder.cardsConnection?.totalCount ?? 0) > 0
      )
    )
  )
}
